/**
 * CosteCatalogoRepo sobre Supabase. Misma lógica que DexieCosteCatalogoRepo.
 */

import type { CosteCatalogoRepo } from "../../repos/CosteCatalogoRepo";
import type { CosteCatalogo } from "@app-types/entities";
import { GERENCIA_DEFAULT } from "@constants/gerencias";
import { SupabaseBaseRepo } from "./SupabaseBaseRepo";

export class SupabaseCosteCatalogoRepo
  extends SupabaseBaseRepo<CosteCatalogo>
  implements CosteCatalogoRepo
{
  constructor(tenantId: string) {
    super("costeCatalogo", tenantId);
  }

  async list(filter?: Partial<CosteCatalogo>): Promise<CosteCatalogo[]> {
    let res = await this.listAll();
    if (filter?.activo !== undefined) res = res.filter((c) => c.activo === filter.activo);
    return res.sort((a, b) => a.codigo.localeCompare(b.codigo));
  }

  async listActivos(): Promise<CosteCatalogo[]> {
    return this.list({ activo: true });
  }

  async getByCodigo(codigo: string): Promise<CosteCatalogo | null> {
    return (await this.listAll()).find((c) => c.codigo === codigo) ?? null;
  }

  async get(id: string): Promise<CosteCatalogo | null> {
    return this.getById(id);
  }

  async create(data: Omit<CosteCatalogo, "id" | "createdAt" | "updatedAt" | "version">): Promise<string> {
    // Unicidad por (gerencia, código): el catálogo es per-gerencia, así que un
    // mismo código puede existir en otra gerencia sin conflicto. Antes la
    // verificación era global y bloqueaba aunque el código no estuviera en la
    // gerencia actual (ni se mostrara en su listado).
    const dup = (await this.listAll()).find(
      (c) =>
        c.codigo === data.codigo &&
        (c.gerenciaId ?? GERENCIA_DEFAULT) === (data.gerenciaId ?? GERENCIA_DEFAULT),
    );
    if (dup)
      throw new Error(
        `Ya existe un coste con código ${data.codigo} en esta gerencia` +
          (dup.activo ? "" : " (está inactivo; reactívelo en lugar de crearlo)"),
      );
    const now = new Date().toISOString();
    const nuevo: CosteCatalogo = {
      ...data,
      id: crypto.randomUUID(),
      tenantId: this.tenantId,
      createdAt: now,
      updatedAt: now,
      version: 1,
    };
    await this.createRaw(nuevo);
    return nuevo.id;
  }

  async update(id: string, data: Partial<CosteCatalogo>, expectedVersion: number): Promise<void> {
    const current = await this.get(id);
    if (!current) throw new Error(`Coste ${id} not found`);
    const updated: CosteCatalogo = {
      ...current,
      ...data,
      // Auditoria de creacion INMUTABLE: ver BaseRepo.update.
      createdBy: current.createdBy,
      createdAt: current.createdAt,
      version: current.version + 1,
      updatedAt: new Date().toISOString(),
    };
    await this.updateRaw(updated, expectedVersion);
  }

  async delete(id: string): Promise<void> {
    await this.deleteById(id);
  }
}
